import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function SlidesPage() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentSlide, setCurrentSlide] = useState({ id: '', image: '', alt: '', link: '' });
  const [token] = useState(localStorage.getItem('adminToken'));

  const fetchSlides = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/slides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch slides');
      }
      const data = await res.json();
      setSlides(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Unauthorized: Admin token missing');
      setLoading(false);
    } else {
      fetchSlides();
    }
  }, [token]);

  const openAddModal = () => {
    setIsEdit(false);
    setCurrentSlide({ id: '', image: '', alt: '', link: '' });
    setShowModal(true);
  };

  const openEditModal = (slide) => {
    setIsEdit(true);
    setCurrentSlide({
      id: slide._id,
      image: slide.image,
      alt: slide.alt,
      link: slide.link,
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSlide((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const url = isEdit
        ? `${API_BASE}/api/admin/slides/${currentSlide.id}`
        : `${API_BASE}/api/admin/slides`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          image: currentSlide.image,
          alt: currentSlide.alt,
          link: currentSlide.link,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'add'} slide`);
      }
      setShowModal(false);
      setSuccess(isEdit ? 'Slide updated successfully' : 'Slide added successfully');
      fetchSlides();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this slide?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/slides/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to delete slide');
      }
      setSlides(slides.filter((slide) => slide._id !== id));
      setSuccess('Slide deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Slides</h2>
        <button className="btn btn-success rounded-pill px-4 py-2" onClick={openAddModal}>
          <i className="bi bi-plus"></i> Add Slide
        </button>
      </div>

      {loading && <div>Loading slides...</div>}
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {!loading && !error && slides.length === 0 && <div>No slides found.</div>}

      {!loading && !error && slides.length > 0 && (
        <div className="table-responsive">
          <table className="table table-striped table-hover align-middle mb-0">
            <thead className="table-success">
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Alt Text</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slides.map((slide, idx) => (
                <tr key={slide._id}>
                  <td>{idx + 1}</td>
                  <td>
                    <img
                      src={slide.image || 'https://via.placeholder.com/60'}
                      alt={slide.alt}
                      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: '0.25rem' }}
                    />
                  </td>
                  <td>{slide.alt}</td>
                  <td>{slide.link}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-success py-2 rounded px-3 my-1 me-2"
                      onClick={() => openEditModal(slide)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger py-2 rounded px-3 my-1"
                      onClick={() => handleDelete(slide._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isEdit ? 'Edit Slide' : 'Add Slide'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Image URL</label>
                    <input
                      type="text"
                      className="form-control"
                      name="image"
                      value={currentSlide.image}
                      onChange={handleInputChange}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Alt Text</label>
                    <input
                      type="text"
                      className="form-control"
                      name="alt"
                      value={currentSlide.alt}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Link</label>
                    <input
                      type="text"
                      className="form-control"
                      name="link"
                      value={currentSlide.link}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {isEdit ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SlidesPage;
